import { getErrorMessage } from '@fruitful/fruitils';
import { useAtomValue } from 'jotai';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronRightIcon,
    H5Emphasized,
    Icon,
    Input,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import { apexAccountUrl, apexTransferUrl } from '../../common/utils/LinkUtils';
import { IS_NON_PROD_ENV } from '../../config/FruitfulEnvironment';
import { adminUserAtom } from '../../entities/entityAtoms';
import {
    useCancelCashJournal,
    useForceApproveCashJournal,
    useForceRejectCashJournal,
} from '../hooks/useCashJournalData';
import type { AdminCashJournalTransferDto } from '../types/ImOpsTypes';
import { CASH_JOURNAL_TERMINAL_STATUSES } from '../types/ImOpsTypes';

interface CashJournalDetailsModalProps {
    journal: AdminCashJournalTransferDto | null;
    isOpen: boolean;
    onClose: () => void;
    onActionSuccess: () => void;
}

type ActiveAction = 'cancel' | 'force-approve' | 'force-reject' | null;

export const CashJournalDetailsModal = ({
    journal,
    isOpen,
    onClose,
    onActionSuccess,
}: CashJournalDetailsModalProps) => {
    const [activeAction, setActiveAction] = useState<ActiveAction>(null);
    const [cancelNote, setCancelNote] = useState('');
    const [forceRejectReason, setForceRejectReason] = useState('');
    const [isStateLogExpanded, setIsStateLogExpanded] = useState(false);

    const adminUser = useAtomValue(adminUserAtom);
    const toast = useToastController();

    const cancelMutation = useCancelCashJournal();
    const forceApproveMutation = useForceApproveCashJournal();
    const forceRejectMutation = useForceRejectCashJournal();

    const isMutating =
        cancelMutation.isPending ||
        forceApproveMutation.isPending ||
        forceRejectMutation.isPending;

    if (journal == null) {
        return null;
    }

    const isTerminal = CASH_JOURNAL_TERMINAL_STATUSES.includes(journal.status);
    const isPosted = journal.apexPostedAt != null;
    const canAct = !isTerminal;
    const canForceAct = !isTerminal && !isPosted;

    const resetActionState = () => {
        setActiveAction(null);
        setCancelNote('');
        setForceRejectReason('');
    };

    const handleActionSuccess = (message: string) => {
        toast.show(message, {
            ...MutationSuccessToastOptions,
            duration: 10000,
        });
        resetActionState();
        onClose();
        onActionSuccess();
    };

    const handleActionError = (message: string) => {
        toast.show(message, {
            ...MutationErrorToastOptions(message),
            duration: 10000,
        });
    };

    const handleConfirmCancel = async () => {
        try {
            const result = await cancelMutation.mutateAsync({
                body: {
                    clientTransferId: journal.clientTransferId,
                    cancellationReason: cancelNote,
                    adminUserEmail: adminUser.email,
                },
            });
            if (result.success) {
                handleActionSuccess(
                    result.message || 'Cash journal cancelled successfully',
                );
            } else {
                handleActionError(
                    result.message || 'Failed to cancel cash journal',
                );
            }
        } catch (error) {
            handleActionError(getErrorMessage(error));
        }
    };

    const handleConfirmForceApprove = async () => {
        try {
            const result = await forceApproveMutation.mutateAsync({
                body: { clientTransferId: journal.clientTransferId },
            });
            handleActionSuccess(
                result.message || 'Force-approve submitted successfully',
            );
        } catch (error) {
            handleActionError(getErrorMessage(error));
        }
    };

    const handleConfirmForceReject = async () => {
        try {
            const result = await forceRejectMutation.mutateAsync({
                body: {
                    clientTransferId: journal.clientTransferId,
                    reason: forceRejectReason || undefined,
                },
            });
            handleActionSuccess(
                result.message || 'Force-reject submitted successfully',
            );
        } catch (error) {
            handleActionError(getErrorMessage(error));
        }
    };

    const isStateLogEntry = (
        entry: Record<string, unknown>,
    ): entry is {
        time: string;
        source: string;
        state: string;
        actor?: string;
        message?: string;
        metadata?: Record<string, unknown>;
    } =>
        typeof entry.time === 'string' &&
        typeof entry.source === 'string' &&
        typeof entry.state === 'string';

    const getSourceColor = (source: string): string => {
        switch (source) {
            case 'ORCHESTRATION':
                return '#2196F3';
            case 'APEX':
                return '#FF9800';
            default:
                return '#000000';
        }
    };

    const header = (
        <XStack justifyContent="space-between" alignItems="center" width="100%">
            <H5Emphasized>Cash Journal Details</H5Emphasized>
        </XStack>
    );

    return (
        <Dialog
            header={header}
            visible={isOpen}
            style={{ width: '50vw' }}
            onHide={onClose}
            draggable={false}
            resizable={false}
        >
            <YStack gap={24} paddingVertical={16}>
                {/* Transfer Identification */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        Transfer Information
                    </BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Client Transfer ID:
                            </BodySmallRegular>
                            <BodySmallRegular fontFamily="monospace">
                                {journal.clientTransferId}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Workflow ID:</BodySmallRegular>
                            <BodySmallRegular fontFamily="monospace">
                                {journal.workflowId ?? '-'}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Purpose:</BodySmallRegular>
                            <BodySmallRegular>
                                {journal.purpose}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>
                                Initiation Source:
                            </BodySmallRegular>
                            <BodySmallRegular>
                                {journal.initiationSource}
                            </BodySmallRegular>
                        </XStack>
                    </YStack>
                </YStack>

                {/* Account Information */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>
                        Account Information
                    </BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Membership ID:</BodySmallRegular>
                            <BodySmallRegular>
                                {journal.membershipId}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>User ID:</BodySmallRegular>
                            <BodySmallRegular>
                                {journal.userId ?? '-'}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                From Apex Account:
                            </BodySmallRegular>
                            <YStack alignItems="flex-end" gap={2}>
                                <a
                                    href={apexAccountUrl(
                                        journal.fromApexAccountId,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ fontSize: '0.875rem' }}
                                >
                                    {journal.fromApexAccountId}
                                </a>
                                <BodySmallRegular color="$gray1000">
                                    {journal.fromApexAccountNumber}
                                </BodySmallRegular>
                            </YStack>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>
                                To Apex Account:
                            </BodySmallRegular>
                            <YStack alignItems="flex-end" gap={2}>
                                <a
                                    href={apexAccountUrl(
                                        journal.toApexAccountId,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ fontSize: '0.875rem' }}
                                >
                                    {journal.toApexAccountId}
                                </a>
                                <BodySmallRegular color="$gray1000">
                                    {journal.toApexAccountNumber}
                                </BodySmallRegular>
                            </YStack>
                        </XStack>
                    </YStack>
                </YStack>

                {/* Amount Details */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Amount Details</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Amount:</BodySmallRegular>
                            <BodyMediumRegular fontWeight="bold">
                                {journal.amountCents != null
                                    ? centsToFormattedDollars(
                                          journal.amountCents,
                                      )
                                    : '-'}
                            </BodyMediumRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>
                                Full Disbursement:
                            </BodySmallRegular>
                            <BodySmallRegular>
                                {journal.fullDisbursement != null
                                    ? journal.fullDisbursement
                                        ? 'Yes'
                                        : 'No'
                                    : '-'}
                            </BodySmallRegular>
                        </XStack>
                    </YStack>
                </YStack>

                {/* Status */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Status</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Orchestration Status:
                            </BodySmallRegular>
                            <BodySmallRegular fontWeight="bold">
                                {journal.status}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>Status Updated:</BodySmallRegular>
                            <BodySmallRegular>
                                {new Date(
                                    journal.statusUpdatedAt,
                                ).toLocaleString()}
                            </BodySmallRegular>
                        </XStack>
                    </YStack>
                </YStack>

                {/* Apex Details */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Apex Details</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>
                                Apex Cash Journal ID:
                            </BodySmallRegular>
                            {journal.apexCashJournalId != null ? (
                                <a
                                    href={apexTransferUrl(
                                        journal.fromApexAccountId,
                                        journal.apexCashJournalId,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        fontSize: '0.875rem',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {journal.apexCashJournalId}
                                </a>
                            ) : (
                                <BodySmallRegular>-</BodySmallRegular>
                            )}
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Apex State:</BodySmallRegular>
                            <BodySmallRegular>
                                {journal.apexCurrentState ?? '-'}
                            </BodySmallRegular>
                        </XStack>
                        {journal.apexPostedAt != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                                borderBottomWidth={1}
                                borderBottomColor="$gray500"
                            >
                                <BodySmallRegular>
                                    Apex Posted:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        journal.apexPostedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                        {journal.apexCompletedAt != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>
                                    Apex Completed:
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        journal.apexCompletedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* Linked ICT */}
                {journal.triggeringIctDepositIdempotencyKey != null && (
                    <YStack gap={8}>
                        <BodyLargeEmphasized>
                            Linked ICT Deposit
                        </BodyLargeEmphasized>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                        >
                            <BodySmallRegular>
                                Idempotency Key:
                            </BodySmallRegular>
                            <BodySmallRegular fontFamily="monospace">
                                {journal.triggeringIctDepositIdempotencyKey}
                            </BodySmallRegular>
                        </XStack>
                    </YStack>
                )}

                {/* Timestamps */}
                <YStack gap={8}>
                    <BodyLargeEmphasized>Timestamps</BodyLargeEmphasized>
                    <YStack gap={0}>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Created:</BodySmallRegular>
                            <BodySmallRegular>
                                {new Date(journal.createdAt).toLocaleString()}
                            </BodySmallRegular>
                        </XStack>
                        <XStack
                            justifyContent="space-between"
                            paddingVertical={8}
                            borderBottomWidth={1}
                            borderBottomColor="$gray500"
                        >
                            <BodySmallRegular>Updated:</BodySmallRegular>
                            <BodySmallRegular>
                                {new Date(journal.updatedAt).toLocaleString()}
                            </BodySmallRegular>
                        </XStack>
                        {journal.completedAt != null && (
                            <XStack
                                justifyContent="space-between"
                                paddingVertical={8}
                            >
                                <BodySmallRegular>Completed:</BodySmallRegular>
                                <BodySmallRegular>
                                    {new Date(
                                        journal.completedAt,
                                    ).toLocaleString()}
                                </BodySmallRegular>
                            </XStack>
                        )}
                    </YStack>
                </YStack>

                {/* State Log - Collapsible */}
                {journal.stateLog.length > 0 && (
                    <YStack gap={8}>
                        <XStack
                            gap={8}
                            alignItems="center"
                            cursor="pointer"
                            onPress={() => {
                                setIsStateLogExpanded(!isStateLogExpanded);
                            }}
                            pressStyle={{ opacity: 0.7 }}
                        >
                            <Icon
                                icon={
                                    isStateLogExpanded
                                        ? ChevronDownIcon
                                        : ChevronRightIcon
                                }
                                size={16}
                                color="$black"
                            />
                            <BodyLargeEmphasized>
                                State Log ({journal.stateLog.length} entries)
                            </BodyLargeEmphasized>
                        </XStack>

                        {isStateLogExpanded && (
                            <YStack
                                gap={8}
                                backgroundColor="$gray100"
                                padding={12}
                                borderRadius={8}
                                maxHeight={400}
                                overflow="scroll"
                            >
                                {journal.stateLog
                                    .filter(isStateLogEntry)
                                    .slice()
                                    .sort(
                                        (a, b) =>
                                            new Date(b.time).getTime() -
                                            new Date(a.time).getTime(),
                                    )
                                    .map((entry, index) => (
                                        <YStack
                                            key={index}
                                            gap={4}
                                            padding={8}
                                            backgroundColor="white"
                                            borderRadius={4}
                                            borderWidth={1}
                                            borderColor="$gray200"
                                        >
                                            <XStack
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <BodySmallRegular
                                                    fontWeight="bold"
                                                    color={getSourceColor(
                                                        entry.source,
                                                    )}
                                                >
                                                    {entry.source}
                                                </BodySmallRegular>
                                                <BodySmallRegular
                                                    fontSize={10}
                                                    color="$gray1000"
                                                >
                                                    {new Date(
                                                        entry.time,
                                                    ).toLocaleString()}
                                                </BodySmallRegular>
                                            </XStack>
                                            <XStack justifyContent="space-between">
                                                <BodySmallRegular>
                                                    State:
                                                </BodySmallRegular>
                                                <BodySmallRegular
                                                    fontWeight="bold"
                                                    fontFamily="monospace"
                                                >
                                                    {entry.state}
                                                </BodySmallRegular>
                                            </XStack>
                                            {entry.actor != null && (
                                                <XStack justifyContent="space-between">
                                                    <BodySmallRegular>
                                                        Actor:
                                                    </BodySmallRegular>
                                                    <BodySmallRegular>
                                                        {entry.actor}
                                                    </BodySmallRegular>
                                                </XStack>
                                            )}
                                            {entry.message != null && (
                                                <YStack gap={2}>
                                                    <BodySmallRegular fontWeight="bold">
                                                        Message:
                                                    </BodySmallRegular>
                                                    <BodySmallRegular
                                                        fontSize={11}
                                                        color="$gray1100"
                                                    >
                                                        {entry.message}
                                                    </BodySmallRegular>
                                                </YStack>
                                            )}
                                            {entry.metadata != null &&
                                                Object.keys(entry.metadata)
                                                    .length > 0 && (
                                                    <YStack gap={2}>
                                                        <BodySmallRegular fontWeight="bold">
                                                            Metadata:
                                                        </BodySmallRegular>
                                                        <BodySmallRegular
                                                            fontSize={10}
                                                            fontFamily="monospace"
                                                            color="$gray1100"
                                                        >
                                                            {JSON.stringify(
                                                                entry.metadata,
                                                                null,
                                                                2,
                                                            )}
                                                        </BodySmallRegular>
                                                    </YStack>
                                                )}
                                        </YStack>
                                    ))}
                            </YStack>
                        )}
                    </YStack>
                )}

                {/* Action Buttons */}
                {canAct && activeAction == null && (
                    <YStack gap={8}>
                        <Button
                            variant="secondary"
                            tone="danger"
                            onPress={() => {
                                setActiveAction('cancel');
                            }}
                        >
                            Cancel Cash Journal
                        </Button>
                        {IS_NON_PROD_ENV && (
                            <XStack gap={12}>
                                <Button
                                    variant="secondary"
                                    onPress={() => {
                                        setActiveAction('force-approve');
                                    }}
                                    disabled={!canForceAct}
                                >
                                    Force Approve
                                </Button>
                                <Button
                                    variant="secondary"
                                    tone="danger"
                                    onPress={() => {
                                        setActiveAction('force-reject');
                                    }}
                                    disabled={!canForceAct}
                                >
                                    Force Reject
                                </Button>
                            </XStack>
                        )}
                        {IS_NON_PROD_ENV && isPosted && (
                            <BodySmallRegular color="$gray1000">
                                Force approve/reject unavailable — journal is
                                already posted.
                            </BodySmallRegular>
                        )}
                    </YStack>
                )}

                {/* Cancel Confirmation */}
                {activeAction === 'cancel' && (
                    <YStack
                        gap={12}
                        backgroundColor="$red200"
                        padding={16}
                        borderRadius={8}
                    >
                        <BodyLargeEmphasized color="$red1100">
                            Cancel Cash Journal?
                        </BodyLargeEmphasized>
                        <BodySmallRegular>
                            Enter a reason for cancellation:
                        </BodySmallRegular>
                        <Input
                            placeholder="Cancellation reason..."
                            value={cancelNote}
                            onChangeText={setCancelNote}
                        />
                        {isMutating && (
                            <XStack justifyContent="center">
                                <Spinner />
                            </XStack>
                        )}
                        <XStack gap={12}>
                            <Button
                                variant="primary"
                                tone="danger"
                                onPress={() => {
                                    void handleConfirmCancel();
                                }}
                                disabled={
                                    cancelNote.trim().length === 0 || isMutating
                                }
                            >
                                Confirm Cancel
                            </Button>
                            <Button
                                variant="secondary"
                                onPress={resetActionState}
                                disabled={isMutating}
                            >
                                Go Back
                            </Button>
                        </XStack>
                    </YStack>
                )}

                {/* Force Approve Confirmation */}
                {activeAction === 'force-approve' && (
                    <YStack
                        gap={12}
                        backgroundColor="$green200"
                        padding={16}
                        borderRadius={8}
                    >
                        <BodyLargeEmphasized color="$green1100">
                            Force Approve Cash Journal?
                        </BodyLargeEmphasized>
                        <BodySmallRegular>
                            This will trigger a force-approve via the Apex test
                            simulation endpoint. The state change will be
                            confirmed by webhook.
                        </BodySmallRegular>
                        {isMutating && (
                            <XStack justifyContent="center">
                                <Spinner />
                            </XStack>
                        )}
                        <XStack gap={12}>
                            <Button
                                variant="primary"
                                onPress={() => {
                                    void handleConfirmForceApprove();
                                }}
                                disabled={isMutating}
                            >
                                Confirm Force Approve
                            </Button>
                            <Button
                                variant="secondary"
                                onPress={resetActionState}
                                disabled={isMutating}
                            >
                                Go Back
                            </Button>
                        </XStack>
                    </YStack>
                )}

                {/* Force Reject Confirmation */}
                {activeAction === 'force-reject' && (
                    <YStack
                        gap={12}
                        backgroundColor="$red200"
                        padding={16}
                        borderRadius={8}
                    >
                        <BodyLargeEmphasized color="$red1100">
                            Force Reject Cash Journal?
                        </BodyLargeEmphasized>
                        <BodySmallRegular>
                            Optionally provide a reason for the rejection:
                        </BodySmallRegular>
                        <Input
                            placeholder="Rejection reason (optional)..."
                            value={forceRejectReason}
                            onChangeText={setForceRejectReason}
                        />
                        {isMutating && (
                            <XStack justifyContent="center">
                                <Spinner />
                            </XStack>
                        )}
                        <XStack gap={12}>
                            <Button
                                variant="primary"
                                tone="danger"
                                onPress={() => {
                                    void handleConfirmForceReject();
                                }}
                                disabled={isMutating}
                            >
                                Confirm Force Reject
                            </Button>
                            <Button
                                variant="secondary"
                                onPress={resetActionState}
                                disabled={isMutating}
                            >
                                Go Back
                            </Button>
                        </XStack>
                    </YStack>
                )}
            </YStack>
        </Dialog>
    );
};
